import React, { useContext, useState, useEffect, useRef } from "react";
import { Button, Alert, Text, Box, Stack, Drawer, Paper, useMantineTheme, Loader, Center } from "@mantine/core";
import {
  createCaptureSdk,
  createCaptureUiWithInstance,
} from "@microblink/capture";
import "@microblink/capture/style.css";
import { uploadImageToS3 } from "../../../../helpers";
import { DataContext } from "../../../../context/DataContext";
import { IconExclamationCircle, IconId, IconCards, IconChevronRight } from "@tabler/icons-react";
import { isMobile, isFirefox } from "react-device-detect";
import FirefoxCameraWarning from "../../../../components/FirefoxCameraWarning";

export default function Microblink({ onComplete, onStart, onUpload, qrScanned }) {
  const { settings, text, data } = useContext(DataContext);
  const [sdk, setSdk] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFirefoxPrompt, setShowFirefoxPrompt] = useState(
    isMobile && isFirefox
  );
  const [showCaptureOverlay, setShowCaptureOverlay] = useState(false);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const captureStateUnsubscribeRef = useRef(null);
  const theme = useMantineTheme();
  const language = (
    data?.language ||
    settings?.language ||
    settings?.text?.language ||
    text?.language ||
    ""
  ).toLowerCase();
  const isJapaneseLanguage = language === "ja";

  // Get current route settings for offer_one_sided option
  const getCurrentRouteSettings = () => {
    const journey = settings?.journey;
    if (!journey?.route) return null;
    // Find route that matches microblink path
    const currentRoute = journey.route.find(route =>
      route.path?.includes("microblink") || route.path?.includes("mitek")
    );
    return currentRoute?.settings || null;
  };

  const routeSettings = getCurrentRouteSettings();
  const offerOneSided = routeSettings?.offer_one_sided === true;

  // Get localized button labels
  const getLocalizedLabel = (labelObj, fallback) => {
    if (!labelObj) return fallback;
    // Try exact match first, then fallback to 'en'
    return labelObj[language] || labelObj.en || fallback;
  };

  const oneSidedSectionTitle = getLocalizedLabel(
    routeSettings?.one_sided_section_title,
    "What type of ID will you use?"
  );
  const oneSidedLabel = getLocalizedLabel(
    routeSettings?.one_sided_button_label,
    "Use single-sided ID"
  );
  const twoSidedLabel = getLocalizedLabel(
    routeSettings?.two_sided_button_label,
    "Use two-sided ID"
  );
  const orLabel = getLocalizedLabel(
    routeSettings?.or_label,
    "or"
  );

  async function processAndUpload(data, width, height, suffix) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.putImageData(new ImageData(data, width, height), 0, 0);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 1.0)
    );
    await uploadImageToS3(`${settings.request_id}_dl_${suffix}.jpeg`, blob, settings);
  }

  // Fire-and-forget stats logging - failures should not block the user flow
  function sendResultToApi(requestId, documentGroup, cameraInfo = null) {
    const payload = {
      request_id: requestId,
      document_group: documentGroup,
    };

    // Add camera info if provided (send raw data without filtering)
    if (cameraInfo) {
      payload.camera_info = {
        name: cameraInfo.name,
        deviceInfo: cameraInfo.deviceInfo,
        settings: cameraInfo.settings,
      };
    }

    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/microblink/stats`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    )
      .then((response) => response.json())
      .then((result) => {
        if (result.result !== "success") {
          console.error("Failed to send result to API:", result);
        }
      })
      .catch((error) => {
        // Log error but don't block - stats are non-critical
        console.error("Error while sending stats to API:", error);
      });
  }

  const captureComponentRef = useRef(null);
  // guard against multiple simultaneous capture sessions
  const isCapturingRef = useRef(false);

  // Fallback license key in case primary key has issues
  const FALLBACK_LICENSE_KEY = "sRwCAAsqLmRjYW1zLmFwcAZsZXlKRGNtVmhkR1ZrVDI0aU9qRTNOalF3T0RRME1qSXhNak1zSWtOeVpXRjBaV1JHYjNJaU9pSXlZemhtTnpCak15MDBPVEJtTFdVd01UUXROR0ppTlMweU9XVmpNbVptTlRkaE5UZ2lmUT093OXef1o3RWwzUKnc1+HaeRjVgR1Wv/gfkuOAeSyuFxwHUgUwewwXcqV33+cVXeNIq/MHGyFxsEz4EN0eG9oBXLgeJvuHnygJOWIq7MjWr828ftm400to2umKIlPl";

  useEffect(() => {
    // Preload the SDK (skip if offerOneSided - we'll load after user selection)
    async function preloadSdk() {
      if (showFirefoxPrompt) return;
      if (offerOneSided) return; // Don't preload - wait for user selection
      setIsLoading(true);

      // Helper function to initialize SDK with a given key
      async function initializeSdkWithKey(licenseKey, keyName) {
        console.log(`Attempting to initialize Microblink SDK with ${keyName} (first 8 chars):`, licenseKey?.substring(0, 8));

        const loadedSdk = await createCaptureSdk({
          licenseKey: licenseKey,
          resourceUrl: `${window.location.protocol}//${window.location.hostname}:${window.location.port}`,
        });

        // Disable blur detection for all desktop devices and specified sites
        if(settings.site_name === 'GAVS-PROD-TEST' ||
           settings.site_name === "GAVS-PROD-RO" ||
           !isMobile){
          await loadedSdk.updateAnalyzerSettings({
            blurPolicy: "disabled", // Disables blur detection
            glarePolicy: "disabled" // Also disable glare detection
          });
        }

        // For Veratad sites, optimize for quality with enhanced settings
        if(settings.site_name && settings.site_name.toLowerCase().includes('veratad')){
          await loadedSdk.updateAnalyzerSettings({
            captureStrategy: "optimize-for-quality",
            adjustMinimumDocumentDpi: true,
            minimumDocumentDpi: 400,
            keepDpiOnTransformedDocumentImage: true,
            lightingThresholds: {
              // Allow darker lighting; frames below this are too dark (0-1 scale)
              tooDarkThreshold: 0.1,
            }
          });
        }

        // For KID environments, allow darker ambient lighting before capture
        if (
          settings.site_name &&
          ["kid-qa", "kid-pre-prod"].includes(
            settings.site_name.toLowerCase()
          )
        ) {
          await loadedSdk.updateAnalyzerSettings({
            lightingThresholds: {
              // Allow darker ambient lighting; frames below this are considered too dark
              tooDarkThreshold: 0.1,
            },
          });
        }

        // For front-only capture, skip the back side
        // Skip this if offerOneSided is true - we'll set it when user makes selection
        if (settings.journey?.settings?.frontIdOnly && !offerOneSided) {
          await loadedSdk.updateAnalyzerSettings({
            captureSingleSide: true,
          });
        }

        return loadedSdk;
      }

      try {
        // Try with primary key first
        const mbKey = process.env.REACT_APP_MB_KEY;
        const loadedSdk = await initializeSdkWithKey(mbKey, "primary key");
        console.log("Microblink SDK initialized successfully with primary key");
        setSdk(loadedSdk);
      } catch (primaryError) {
        console.warn("Primary license key failed:", primaryError.message);

        // Check if it's a license-related error and try fallback
        const isLicenseError = primaryError.message?.toLowerCase().includes('license') ||
                               primaryError.message?.toLowerCase().includes('expired') ||
                               primaryError.message?.toLowerCase().includes('invalid');

        if (isLicenseError) {
          console.log("Attempting fallback license key...");
          try {
            const loadedSdk = await initializeSdkWithKey(FALLBACK_LICENSE_KEY, "fallback key");
            console.log("Microblink SDK initialized successfully with fallback key");
            setSdk(loadedSdk);
          } catch (fallbackError) {
            console.error("Fallback license key also failed:", fallbackError.message);
            sendResultToApi(settings.request_id, fallbackError.message);
            setErrorMessage("Failed to initialize the SDK: " + fallbackError.message);
          }
        } else {
          // Non-license error, don't retry
          sendResultToApi(settings.request_id, primaryError.message);
          setErrorMessage("Failed to initialize the SDK: " + primaryError.message);
        }
      }
      setIsLoading(false);
    }

    preloadSdk();

    // Cleanup on component unmount
    return () => {
      captureStateUnsubscribeRef.current?.();
      captureComponentRef.current?.dismount();
      setShowCaptureOverlay(false);
    };
  }, [showFirefoxPrompt]);

  // Start capture with a provided SDK instance (used when SDK is loaded on-demand)
  async function startCaptureWithSdk(sdkInstance, captureMode) {
    if (isCapturingRef.current) return;
    isCapturingRef.current = true;
    onStart();

    if (isJapaneseLanguage) {
      setShowCaptureOverlay(true);
    }

    await sendResultToApi(settings.request_id, `SDK loaded - capture mode: ${captureMode}`);

    try {
      const component = await createCaptureUiWithInstance(sdkInstance, {
        target: undefined,
        showTutorial: false,
        showErrorDialog: false,
        destroyInstanceOnDismount: true,
        localization: {
          ...text.captureMessages,
          ...text.tutorialMessages,
          ...text.cameraMessages,
          ...text.sharedMessages,
        },
      });

      const originalDismount = component.dismount.bind(component);
      component.dismount = () => {
        captureStateUnsubscribeRef.current?.();
        setShowCaptureOverlay(false);
        setIsLoading(false);
        isCapturingRef.current = false;
        return originalDismount();
      };

      captureStateUnsubscribeRef.current?.();
      captureStateUnsubscribeRef.current = component.captureSdk?.subscribe(
        (s) => s.isPlaying || s.isCapturing,
        (isActive) => {
          if (isActive && isJapaneseLanguage) {
            setShowCaptureOverlay(true);
          } else if (!isActive) {
            setShowCaptureOverlay(false);
            setIsLoading(false);
            isCapturingRef.current = false;
          }
        }
      );

      component.captureSdk?.subscribe(
        (s) => s.errorState,
        async (x) => {
          if (x?.message !== undefined) {
            setErrorMessage(x?.message);
            console.error("Error from the SDK: ", x?.message);
            try {
              await sendResultToApi(settings.request_id, x?.message);
            } catch (error) {
              console.error("Error sending result to API:", error);
            }
            component.dismount();
            setIsLoading(false);
            isCapturingRef.current = false;
            setShowCaptureOverlay(false);
          }
        }
      );

      component.captureSdk?.setCallbacks({
        onCaptureResult: async (result) => {
          const selectedCamera = component.captureSdk?.getState().selectedCamera;
          await sendResultToApi(settings.request_id, result.documentGroup, selectedCamera);

          if (settings.journey.settings?.checkMbDocumentGroup && result.documentGroup === "unknown") {
            component.dismount();
            setErrorMessage("The document type is unknown. Please try again.");
            setIsLoading(false);
            isCapturingRef.current = false;
            setShowCaptureOverlay(false);
            return;
          }

          component.dismount();
          onUpload();

          // Build array of upload promises for parallel execution
          const uploadPromises = [
            processAndUpload(
              result.firstCapture.imageResult.data,
              result.firstCapture.imageResult.width,
              result.firstCapture.imageResult.height,
              "front"
            )
          ];

          if (result.secondCapture?.imageResult) {
            uploadPromises.push(
              processAndUpload(
                result.secondCapture.imageResult.data,
                result.secondCapture.imageResult.width,
                result.secondCapture.imageResult.height,
                "back"
              )
            );
          }

          // Upload front and back images in parallel
          await Promise.all(uploadPromises);

          onComplete();
          setIsLoading(false);
          setShowCaptureOverlay(false);
          isCapturingRef.current = false;
        },
      });

      captureComponentRef.current = component;
    } catch (error) {
      await sendResultToApi(settings.request_id, error.message);
      setErrorMessage("Failed to start the capture process: " + error.message);
      setIsLoading(false);
      setShowCaptureOverlay(false);
      isCapturingRef.current = false;
    }
  }

  async function startCapture(selectedCaptureMode = null) {
    // prevent starting the capture process if one is already running
    if (isCapturingRef.current) {
      return;
    }
    isCapturingRef.current = true;
    setIsLoading(true);
    setDrawerOpened(false);
    onStart();
    if (!sdk) {
      sendResultToApi(
        settings.request_id,
        "SDK not initialized, please reload the page."
      );
      setErrorMessage("SDK not initialized, please reload the page.");
      setIsLoading(false);
      isCapturingRef.current = false;
      return;
    }
    if (isJapaneseLanguage) {
      setShowCaptureOverlay(true);
    }

    // Apply capture mode setting if offer_one_sided is enabled and mode was selected
    if (offerOneSided && selectedCaptureMode) {
      const shouldCaptureSingleSide = selectedCaptureMode === "one_sided";
      await sdk.updateAnalyzerSettings({
        captureSingleSide: shouldCaptureSingleSide,
      });
      await sendResultToApi(settings.request_id, `SDK loaded - capture mode: ${selectedCaptureMode}`);
    } else {
      await sendResultToApi(settings.request_id, "SDK loaded");
    }

    try {
      const component = await createCaptureUiWithInstance(sdk, {
        target: undefined,
        showTutorial: false,
        showErrorDialog: false,
        destroyInstanceOnDismount: true,
        localization: {
          ...text.captureMessages,
          ...text.tutorialMessages,
          ...text.cameraMessages,
          ...text.sharedMessages,
        },
      });

      const originalDismount = component.dismount.bind(component);
      component.dismount = () => {
        captureStateUnsubscribeRef.current?.();
        setShowCaptureOverlay(false);
        setIsLoading(false);
        isCapturingRef.current = false;
        return originalDismount();
      };

      captureStateUnsubscribeRef.current?.();
      captureStateUnsubscribeRef.current = component.captureSdk?.subscribe(
        (s) => s.isPlaying || s.isCapturing,
        (isActive) => {
          if (isActive && isJapaneseLanguage) {
            setShowCaptureOverlay(true);
          } else if (!isActive) {
            setShowCaptureOverlay(false);
            // Ensure the intro button becomes clickable again when SDK is closed
            setIsLoading(false);
            isCapturingRef.current = false;
          }
        }
      );

      component.captureSdk?.subscribe(
        (s) => s.errorState,
        async (x) => {
          if (x?.message !== undefined) {
            setErrorMessage(x?.message);
            console.error("Error from the SDK: ", x?.message);

            try {
              sendResultToApi(settings.request_id, x?.message);
            } catch (error) {
              console.error("Error sending result to API:", error);
            }

            component.dismount();
            setIsLoading(false);
            isCapturingRef.current = false;
            setShowCaptureOverlay(false);
          }
        }
      );

      component.captureSdk?.setCallbacks({
        onCaptureResult: async (result) => {
          // Get camera info from SDK state
          const selectedCamera = component.captureSdk?.getState().selectedCamera;

          sendResultToApi(settings.request_id, result.documentGroup, selectedCamera);

          if (
            settings.journey.settings?.checkMbDocumentGroup &&
            result.documentGroup === "unknown"
          ) {
            component.dismount();
            setErrorMessage("The document type is unknown. Please try again.");
            setIsLoading(false);
            isCapturingRef.current = false;
            setShowCaptureOverlay(false);
            return;
          }

          component.dismount();
          onUpload();

          // Build array of upload promises for parallel execution
          const uploadPromises = [
            processAndUpload(
              result.firstCapture.imageResult.data,
              result.firstCapture.imageResult.width,
              result.firstCapture.imageResult.height,
              "front"
            )
          ];

          if (result.secondCapture?.imageResult) {
            uploadPromises.push(
              processAndUpload(
                result.secondCapture.imageResult.data,
                result.secondCapture.imageResult.width,
                result.secondCapture.imageResult.height,
                "back"
              )
            );
          }

          // Upload front and back images in parallel
          await Promise.all(uploadPromises);

          onComplete();
          setIsLoading(false);
          setShowCaptureOverlay(false);
          isCapturingRef.current = false;
        },
      });

      captureComponentRef.current = component;
    } catch (error) {
      sendResultToApi(settings.request_id, error.message);
      setErrorMessage("Failed to start the capture process: " + error.message);
      setIsLoading(false);
      setShowCaptureOverlay(false);
      isCapturingRef.current = false;
    }
  }

  // Handle button click - show drawer if offerOneSided, otherwise start capture
  const handleButtonClick = () => {
    if (offerOneSided) {
      setDrawerOpened(true);
    } else {
      startCapture();
    }
  };

  // Handle document type selection from drawer - load SDK then start capture
  const handleDocumentTypeSelection = async (mode) => {
    setDrawerLoading(true);

    try {
      const mbKey = process.env.REACT_APP_MB_KEY;
      console.log('Microblink SDK key (first 8 chars):', mbKey?.substring(0, 8));

      const loadedSdk = await createCaptureSdk({
        licenseKey: mbKey,
        resourceUrl: `${window.location.protocol}//${window.location.hostname}:${window.location.port}`,
      });

      // Apply standard settings
      if (settings.site_name === 'GAVS-PROD-TEST' ||
          settings.site_name === "GAVS-PROD-RO" ||
          !isMobile) {
        await loadedSdk.updateAnalyzerSettings({
          blurPolicy: "disabled",
          glarePolicy: "disabled"
        });
      }

      if (settings.site_name && settings.site_name.toLowerCase().includes('veratad')) {
        await loadedSdk.updateAnalyzerSettings({
          captureStrategy: "optimize-for-quality",
          adjustMinimumDocumentDpi: true,
          minimumDocumentDpi: 400,
          keepDpiOnTransformedDocumentImage: true,
          lightingThresholds: {
            tooDarkThreshold: 0.1,
          }
        });
      }

      if (settings.site_name &&
          ["kid-qa", "kid-pre-prod"].includes(settings.site_name.toLowerCase())) {
        await loadedSdk.updateAnalyzerSettings({
          lightingThresholds: {
            tooDarkThreshold: 0.1,
          },
        });
      }

      // Apply single-side setting based on user selection
      const shouldCaptureSingleSide = mode === "one_sided";
      await loadedSdk.updateAnalyzerSettings({
        captureSingleSide: shouldCaptureSingleSide,
      });

      setSdk(loadedSdk);

      // Now start capture with the loaded SDK
      setDrawerOpened(false);
      setDrawerLoading(false);
      setIsLoading(true);
      setTimeout(() => {
        startCaptureWithSdk(loadedSdk, mode);
      }, 100);

    } catch (error) {
      await sendResultToApi(settings.request_id, error.message);
      setErrorMessage("Failed to initialize the SDK: " + error.message);
      setDrawerOpened(false);
      setDrawerLoading(false);
      setIsLoading(false);
    }
  };

  if (showFirefoxPrompt) {
    return (
      <FirefoxCameraWarning onContinue={() => setShowFirefoxPrompt(false)} />
    );
  }

  return (
    <>
      {errorMessage && (
        <Alert
          icon={<IconExclamationCircle />}
          color="red"
          title={errorMessage}
        >
          {errorMessage !==
            "The document type is unknown. Please try again." && (
            <Text>{text.dcams.errors.permissions}</Text>
          )}
        </Alert>
      )}

      <Button
        size="lg"
        fullWidth
        onClick={handleButtonClick}
        disabled={offerOneSided ? (isLoading || qrScanned) : (!sdk || isLoading || qrScanned)}
        loading={offerOneSided ? isLoading : (!sdk || isLoading)}
        loaderPosition="left"
      >
        {text.dcams.button}
      </Button>

      {/* Document type selection drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        position="bottom"
        size="auto"
        withCloseButton={false}
        styles={{
          overlay: {
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            backdropFilter: "blur(2px)",
          },
          inner: {
            padding: "0 8px 8px 8px",
          },
          content: {
            borderRadius: "12px !important",
            border: "1px solid #d0d0d0",
            boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
          },
          body: {
            padding: "20px 20px 28px 20px",
          },
        }}
      >
        <Stack spacing="md">
          {drawerLoading ? (
            <Center py="xl">
              <Stack align="center" spacing="md">
                <Loader size="md" />
                <Text size="sm" c="dimmed">
                  {language === "ja" ? "準備中..." : "Getting ready..."}
                </Text>
              </Stack>
            </Center>
          ) : (
            <>
              <Text fw={600} size="lg" ta="center">
                {oneSidedSectionTitle}
              </Text>

              <Stack spacing="sm">
                <Paper
                  onClick={() => handleDocumentTypeSelection("one_sided")}
                  p="md"
                  withBorder
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: theme.colors.blue[5],
                      backgroundColor:
                        theme.colorScheme === "dark"
                          ? theme.colors.dark[5]
                          : theme.colors.gray[0],
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <IconId size={28} style={{ marginRight: 12, flexShrink: 0, color: theme.colors.blue[6] }} />
                      <Text fw={500}>{oneSidedLabel}</Text>
                    </Box>
                    <IconChevronRight size={20} style={{ flexShrink: 0, color: theme.colors.gray[5] }} />
                  </Box>
                </Paper>

                <Text size="sm" c="dimmed" ta="center" py={4}>
                  {orLabel}
                </Text>

                <Paper
                  onClick={() => handleDocumentTypeSelection("two_sided")}
                  p="md"
                  withBorder
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: theme.colors.blue[5],
                      backgroundColor:
                        theme.colorScheme === "dark"
                          ? theme.colors.dark[5]
                          : theme.colors.gray[0],
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <IconCards size={28} style={{ marginRight: 12, flexShrink: 0, color: theme.colors.blue[6] }} />
                      <Text fw={500}>{twoSidedLabel}</Text>
                    </Box>
                    <IconChevronRight size={20} style={{ flexShrink: 0, color: theme.colors.gray[5] }} />
                  </Box>
                </Paper>
              </Stack>
            </>
          )}
        </Stack>
      </Drawer>
      {showCaptureOverlay && isJapaneseLanguage && (
        <Box
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 16,
            display: "flex",
            justifyContent: "center",
            zIndex: 9999,
            pointerEvents: "none",
          }}
        >
          <Box
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              color: "white",
              padding: "12px 16px",
              borderRadius: 8,
              maxWidth: 520,
              boxShadow: "0 6px 18px rgba(0, 0, 0, 0.35)",
            }}
          >
            <Text align="center" fz="sm">
              マイナンバーカードの場合は、
              <Text span fw={700}>個人番号およびQR</Text>
              が隠されていることを確認してください。
            </Text>
          </Box>
        </Box>
      )}
    </>
  );
}
